import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Plug, ExternalLink, History, Unlink, Loader2,
} from 'lucide-react';
import {
  connectionsApi,
  oauthConsentsApi,
  type ConnectionSummary,
  type McpActivityEntry,
  type OAuthConsent,
} from '../api/services';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// ConnectionsPage — Phase 3 task 3.7. Lists every API key on the current
// user's account with a 7-day activity summary, and opens a modal per key
// with the most recent raw requests. Also shows OAuth-connected apps
// (Claude Desktop etc.) with a disconnect button.
// ---------------------------------------------------------------------------

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return iso;
  }
}

function shortenPath(path: string): string {
  if (path.length <= 48) return path;
  return path.slice(0, 24) + '…' + path.slice(-22);
}

function methodTone(method: string): string {
  switch (method) {
    case 'GET': return 'bg-blue-600 text-white';
    case 'POST': return 'bg-emerald-600 text-white';
    case 'PUT': return 'bg-amber-600 text-white';
    case 'DELETE': return 'bg-rose-600 text-white';
    case 'PATCH': return 'bg-purple-600 text-white';
    default: return 'bg-muted-foreground text-white';
  }
}

export default function ConnectionsPage() {
  const [connections, setConnections] = useState<ConnectionSummary[]>([]);
  const [consents, setConsents] = useState<OAuthConsent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openKeyId, setOpenKeyId] = useState<string | null>(null);
  const [activity, setActivity] = useState<McpActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [connList, consentList] = await Promise.all([
        connectionsApi.list(),
        oauthConsentsApi.list().catch(() => [] as OAuthConsent[]),
      ]);
      setConnections(connList);
      setConsents(consentList);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load connections.');
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeConsent = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`Disconnect ${name}? It will lose access immediately.`)) return;
    try {
      await oauthConsentsApi.revoke(id);
      setConsents((prev) => prev.filter((c) => c.id !== id));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not revoke consent.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openActivity = useCallback(async (keyId: string) => {
    setOpenKeyId(keyId);
    setActivityLoading(true);
    try {
      const data = await connectionsApi.activity(keyId, 100);
      setActivity(data);
    } catch {
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const activeKey = useMemo(
    () => connections.find((c) => c.keyId === openKeyId) ?? null,
    [connections, openKeyId],
  );

  return (
    <div className="mx-auto max-w-[1100px] p-4 md:p-6">
      <div className="mb-1 flex items-center gap-2">
        <Plug className="h-7 w-7 text-primary" />
        <h1 className="text-2xl font-semibold tracking-tight">Connections</h1>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        External clients with access to your Cadence tenant over MCP, what they've done recently, and when they last checked in.
      </p>

      {error && (
        <Alert variant="error" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && consents.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-2 text-lg font-semibold">Connected apps</h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Apps that connected via OAuth (e.g. Claude Desktop). Disconnect to revoke access immediately.
          </p>
          <div className="flex flex-col gap-3">
            {consents.map((c) => (
              <Card key={c.id}>
                <CardContent>
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold">{c.clientName}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.scopes.split(' ').filter(Boolean).map((s) => (
                          <span key={s} className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[10px]">
                            {s}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-6">
                        <MetaStat label="Connected" value={format(new Date(c.grantedAt), 'd MMM yyyy')} />
                        <MetaStat label="Last used" value={relativeTime(c.lastUsedAt)} />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => revokeConsent(c.id, c.clientName)}
                      className="shrink-0 gap-1.5 text-amber-700 dark:text-amber-400"
                    >
                      <Unlink className="h-4 w-4" />
                      Disconnect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {!loading && consents.length > 0 && connections.length > 0 && (
        <h2 className="mb-3 mt-2 text-lg font-semibold">API keys</h2>
      )}

      {!loading && connections.length === 0 && consents.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Plug className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="mb-1 text-base font-semibold text-muted-foreground">
              No connected apps or API keys yet
            </p>
            <p className="mx-auto mt-1 max-w-[520px] text-sm text-muted-foreground">
              Connect Claude Desktop or another OAuth-capable client by pointing it at your Cadence MCP URL —
              or create an API key in Preferences for scripts and CLI tools.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && connections.length > 0 && (
        <div className="flex flex-col gap-3">
          {connections.map((conn) => (
            <Card key={conn.keyId}>
              <CardContent>
                <div className="flex flex-col gap-3 md:flex-row md:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-base font-semibold">{conn.name}</p>
                      <span className="inline-flex items-center rounded-full bg-muted/40 px-2 py-0.5 font-mono text-[11px]">
                        {conn.keyPrefix}
                      </span>
                      {conn.scopes && (
                        <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                          {conn.scopes}
                        </span>
                      )}
                      {conn.expiresAt && new Date(conn.expiresAt) < new Date() && (
                        <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-6">
                      <MetaStat label="Last seen" value={relativeTime(conn.lastActivityAt)} />
                      <MetaStat label="Calls (7 days)" value={conn.callsLast7Days.toString()} />
                      <MetaStat label="Created" value={format(new Date(conn.createdAt), 'd MMM yyyy')} />
                      {conn.expiresAt && (
                        <MetaStat label="Expires" value={format(new Date(conn.expiresAt), 'd MMM yyyy')} />
                      )}
                    </div>
                    {conn.topPaths.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                          Top paths this week
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {conn.topPaths.map((tp) => (
                            <span
                              key={tp.path}
                              className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 font-mono text-[11px]"
                            >
                              {shortenPath(tp.path)} · {tp.count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openActivity(conn.keyId)}
                    className="shrink-0 gap-1.5"
                  >
                    <History className="h-4 w-4" />
                    Activity
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openKeyId !== null} onOpenChange={(o) => !o && setOpenKeyId(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeKey ? `Activity · ${activeKey.name}` : 'Activity'}</DialogTitle>
          </DialogHeader>
          <div className="border-t border-border pt-4">
            {activityLoading ? (
              <div className="py-8 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : activity.length === 0 ? (
              <p className="py-4 text-sm italic text-muted-foreground">
                No recorded calls for this key yet.
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Method</th>
                      <th className="px-3 py-2">Path</th>
                      <th className="px-3 py-2 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((entry) => (
                      <tr key={entry.id} className="border-b border-border last:border-b-0">
                        <td className="whitespace-nowrap px-3 py-2">
                          <p>{format(new Date(entry.timestamp), 'd MMM HH:mm:ss')}</p>
                          <p className="text-xs text-muted-foreground">{relativeTime(entry.timestamp)}</p>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px]',
                              methodTone(entry.method),
                            )}
                          >
                            {entry.method}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-[12px]">{entry.path}</td>
                        <td
                          className={cn(
                            'px-3 py-2 text-right font-mono',
                            entry.statusCode >= 400 && 'font-semibold text-rose-600 dark:text-rose-400',
                          )}
                        >
                          {entry.statusCode}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {activeKey && (
              <div className="mt-4 flex items-center gap-2 text-muted-foreground">
                <ExternalLink className="h-3.5 w-3.5" />
                <p className="text-xs">
                  Revoke or rotate this key from Preferences. Activity is retained and searchable here.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <p className="text-sm">{value}</p>
    </div>
  );
}
